// Footer.tsx
import React, { useEffect, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { footerData } from "../data/FooterData";
import "../customCSS/Footer.css";
import { sanitizeForUrl } from "../utils/urlSanitizer";
import { fetchCategories } from "../api/categoryApiService";
import { CategoryItem } from "../types/category.types";
import { LOGO_URL, LOGO_FALLBACK } from "../constants/assets";

interface FooterLink {
  label: string;
  url: string;
}

const MAX_FOOTER_CATEGORIES = 8;

const Footer: React.FC = () => {
  const [categories, setCategories] = useState<CategoryItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        if (!cancelled) setCategories(data);
      } catch (err) {
        console.error("Failed to load footer categories:", err);
        if (!cancelled) setCategories([]);
      }
    };

    void loadCategories();
    return () => { cancelled = true; };
  }, []);

  const getCategoryName = (item: unknown): string => {
    if (!item) return "";
    if (typeof item === "string") return item;
    if (typeof item !== "object") return "";

    const category = item as CategoryItem;
    return String(category.name ?? category.title ?? "");
  };

  const buildCategoryRoute = (categoryName: string): string => {
    const slug = sanitizeForUrl(categoryName || "");
    const encodedCategory = encodeURIComponent(categoryName || "");
    return `/search/category/${slug}?category=${encodedCategory}`;
  };

  const getStaticCategoryUrl = (item: CategoryItem | FooterLink): string => {
    return "url" in item && typeof item.url === "string" ? item.url : "";
  };

  const getStaticCategoryLabel = (item: CategoryItem | FooterLink): string => {
    return "label" in item && typeof item.label === "string" ? item.label : "";
  };

  return (
    <footer className="footer-header text-light pt-5 pb-2" role="contentinfo" aria-label="Site Footer">
      <div className="container">
        <div className="row g-4 align-items-start footer-content">
          <div className="col-lg-4 col-md-6">
            <a href="/" className="group flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-gold/50">
                <span className="h-1.5 w-1.5 rounded-full bg-gold-bright" />
              </span>
              <span className="font-serif text-xl tracking-wide-luxe text-bone">
                Sardar<span className="gold-text font-semibold">Times</span>94
              </span>
            </a>
            <br />
            {/* <span className="footer-title-logo">Sardar Times94</span> */}
            <p className="mb-0 text-white-50">
              Sardar Times94 is your ultimate destination for premium watches and stylish accessories, offering a carefully curated collection of classic, modern, and luxury timepieces along with high-quality accessories designed to complement every style and occasion.

            </p>
          </div>

          {footerData.sections.map((section, idx) => (
            <div key={idx} className="col-lg-2 col-md-3 col-6">
              <p className="text-uppercase fw-bold mb-2 footer-title">{section.title}</p>
              <ul className="list-unstyled footer-links">
                {section.title?.toLowerCase() === "categories" ? (
                  // Prefer API-driven categories; fallback to static links
                  (categories.length > 0 ? categories : section.links).slice(0, MAX_FOOTER_CATEGORIES).map((linkOrCat, i: number) => {
                    const name = getCategoryName(linkOrCat) || getStaticCategoryLabel(linkOrCat);
                    const href = getStaticCategoryUrl(linkOrCat) && categories.length === 0
                      ? getStaticCategoryUrl(linkOrCat)
                      : buildCategoryRoute(name);

                    return (
                      <li key={i} className="mb-1">
                        <a href={href} className="text-light text-decoration-none">
                          {name}
                        </a>
                      </li>
                    );
                  })
                ) : (
                  section.links.map((link, i) => (
                    <li key={i} className="mb-1">
                      <a href={link.url} className="text-light text-decoration-none">
                        {link.label}
                      </a>
                    </li>
                  ))
                )}
              </ul>
            </div>
          ))}
        </div>

        {/* <Row className="pt-4 border-top border-white">
          <Col md={4} className="d-flex flex-column align-items-center align-items-md-start mb-3 mb-md-0">
            <div className="mb-2">
              <i className="bi bi-truck fs-4 text-pink"></i>
            </div>
            <div>
              <strong>FREE SHIPPING</strong>
              <p className="mb-0">On Orders Above ₹2999</p>
            </div>
          </Col>

          <Col md={4} className="d-flex flex-column align-items-center align-items-md-start mb-3 mb-md-0">
            <div className="mb-2">
              <i className="bi bi-arrow-counterclockwise fs-4 text-pink"></i>
            </div>
            <div>
              <strong>EASY RETURNS</strong>
              <p className="mb-0">15-Day Return Policy</p>
            </div>
          </Col>

          <Col md={4} className="d-flex flex-column align-items-center align-items-md-start mb-3 mb-md-0">
            <div className="mb-2">
              <i className="bi bi-award fs-4 text-pink"></i>
            </div>
            <div>
              <strong>100% AUTHENTIC</strong>
              <p className="mb-0">Products Sourced Directly</p>
            </div>
          </Col>
        </Row> */}

        {/* <div className="social-media-section "> */}
          <Row className=" row pt-3 mt-3 border-top border-white text-center">
            <div className="text-center mb-3">
              <p>Show us some love on social media ❤️</p>
              {footerData.social.map((social, i) => (
                <a
                  key={i}
                  href={social.url}
                  className="text-light me-3 fs-5"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.platform ?? `Open social link ${i + 1}`}
                  title={social.platform ?? undefined}
                >
                  <i className={social.icon} aria-hidden></i>
                </a>
              ))}
            </div>
          </Row>

          {/* <div className="text-center mb-2">
          {footerData.legal.map((item, i) => (
            <a
              key={i}
              href={item.url}
              className="text-light text-decoration-none mx-2"
            >
              {item.label}
            </a>
          ))}
        </div> */}

          <div className="text-center small">
            {footerData.copyright}
          </div>
        </div>
      {/* </div> */}
    </footer>
  );
};

export default Footer;
